import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

// WebsitePreviewDrawer is the right-anchored slide-in panel that hosts
// a live iframe of the public site. Used by the Designläge editor (where
// the iframe also carries a postMessage bridge for inline editing) and
// by the website wizard (where it's a passive preview alongside
// per-step edits).
//
// Open / closed state and the drawer's current pixel width are owned by
// the host because the host typically wants to reserve padding on its
// own content container so editor content doesn't slide under the
// drawer when it's open. Device preset + drag-resize behaviour are
// internal: they're shared concerns no host wants to re-implement.
//
// The drawer is `position: fixed; right: 0` — it floats over whatever
// layout the host provides. Hosts that want their content to stay
// visible beside it should set `padding-right: ${open ? width : 0}px`
// on their primary content wrapper.
//
// The iframe ref is forwarded so hosts can call setSrc / postMessage
// directly (the design-mode page uses it to scroll the inline-edit
// target into view, etc.).

type Device = 'phone' | 'tablet' | 'desktop' | 'custom';

const DEVICE_WIDTHS: Record<Exclude<Device, 'custom'>, number> = {
  phone: 440,
  tablet: 860,
  desktop: 1280,
};
const MIN_DRAWER_WIDTH = 320;
// Reserve some room for the host's primary content even at the widest
// drawer setting — without this, drag-to-full-screen leaves the editor
// inaccessible.
const MIN_PANEL_GAP = 360;

interface Props {
  open: boolean;
  onClose: () => void;
  width: number;
  onWidthChange: (next: number) => void;
  // The URL the iframe should render. Pass an empty string to surface
  // the "preview unavailable" placeholder — e.g. before the website
  // has been saved and has a preview_token.
  previewURL: string;
  // Optional forwarded ref so the host can manipulate the iframe
  // directly (postMessage, src reload, etc.). The drawer also calls
  // refreshPreview itself on device-preset switches.
  iframeRef?: React.MutableRefObject<HTMLIFrameElement | null>;
  // Initial device preset. Defaults to 'tablet' which matches the
  // historical design-mode behaviour.
  initialDevice?: Exclude<Device, 'custom'>;
}

// clampWidth keeps the drawer between MIN_DRAWER_WIDTH and the largest
// width that still leaves MIN_PANEL_GAP px of room for the host's
// content. Computed against window.innerWidth at call time so resizing
// the browser self-corrects.
function clampWidth(w: number): number {
  const maxByViewport = window.innerWidth - MIN_PANEL_GAP;
  const max = Math.max(MIN_DRAWER_WIDTH, maxByViewport);
  return Math.min(Math.max(w, MIN_DRAWER_WIDTH), max);
}

export function WebsitePreviewDrawer({
  open,
  onClose,
  width,
  onWidthChange,
  previewURL,
  iframeRef: forwardedRef,
  initialDevice = 'tablet',
}: Props) {
  const { t } = useTranslation('common');
  const [device, setDevice] = useState<Device>(initialDevice);
  // Local iframe ref. If the host passes one in we mirror it on every
  // attach so both refs see the live element.
  const internalIframeRef = useRef<HTMLIFrameElement | null>(null);
  // Drag state for the resize handle. Stored in a ref because the
  // pointer-move handler reads it on every move without needing a
  // re-render between events. setPointerCapture on the handle keeps
  // events flowing even when the cursor leaves the element.
  const dragStartRef = useRef<{ x: number; width: number } | null>(null);

  const setIframeRef = useCallback(
    (el: HTMLIFrameElement | null) => {
      internalIframeRef.current = el;
      if (forwardedRef) {
        forwardedRef.current = el;
      }
    },
    [forwardedRef],
  );

  const refreshPreview = useCallback(() => {
    // Bumping src forces the iframe to reload. CSS media queries auto-
    // update on iframe resize, but some responsive bits (open mobile
    // menu state, the public site's JS-driven teams dropdown, anchored
    // scroll positions) get stuck in their previous viewport's mode —
    // a clean reload resets everything.
    const f = internalIframeRef.current;
    if (!f) return;
    const cur = f.src;
    f.src = '';
    setTimeout(() => {
      if (internalIframeRef.current) internalIframeRef.current.src = cur;
    }, 0);
  }, []);

  const pickDevice = useCallback(
    (d: Exclude<Device, 'custom'>) => {
      setDevice(d);
      onWidthChange(clampWidth(DEVICE_WIDTHS[d]));
    },
    [onWidthChange],
  );

  const onHandlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      dragStartRef.current = { x: e.clientX, width };
    },
    [width],
  );

  const onHandlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (!dragStartRef.current) return;
      // Handle sits on the LEFT edge of the right-anchored drawer, so
      // dragging left grows the drawer and dragging right shrinks it.
      const dx = dragStartRef.current.x - e.clientX;
      onWidthChange(clampWidth(dragStartRef.current.width + dx));
      setDevice('custom');
    },
    [onWidthChange],
  );

  const onHandlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId);
      }
      dragStartRef.current = null;
    },
    [],
  );

  // Recompute clamp when the viewport changes so a maximised drawer
  // doesn't escape the available space on resize.
  useEffect(() => {
    function onResize() {
      onWidthChange(clampWidth(width));
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onWidthChange, width]);

  // Reload the iframe when the user switches to a different device
  // preset. We deliberately don't refresh while transitioning into
  // 'custom' (which the drag handle does on every pointer-move) — that
  // would thrash the iframe with a reload per mouse pixel.
  const prevDeviceRef = useRef<Device>(device);
  useEffect(() => {
    const prev = prevDeviceRef.current;
    prevDeviceRef.current = device;
    if (prev === device) return;
    if (device === 'custom') return;
    refreshPreview();
  }, [device, refreshPreview]);

  // The drawer uses CSS class names from global.css (design-drawer-*).
  // Renamed to website-preview-drawer in a follow-up; the old class
  // names are kept here so the existing styling lines up without a CSS
  // migration in this change.
  return (
    <aside
      className={`design-drawer${open ? ' design-drawer--open' : ''}`}
      aria-hidden={!open}
      style={{ width: `${width}px` }}
    >
      <div
        className="design-drawer__handle"
        role="separator"
        aria-orientation="vertical"
        aria-label={t('website.design.previewResize')}
        onPointerDown={onHandlePointerDown}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
      />
      <div className="design-drawer__toolbar">
        <div
          className="design-drawer__devices"
          role="tablist"
          aria-label={t('website.design.previewTitle')}
        >
          {(['phone', 'tablet', 'desktop'] as const).map((d) => (
            <button
              key={d}
              type="button"
              role="tab"
              aria-selected={device === d}
              className={`design-drawer__device${device === d ? ' design-drawer__device--active' : ''}`}
              onClick={() => pickDevice(d)}
            >
              {t(`website.design.previewDevice${d.charAt(0).toUpperCase() + d.slice(1)}`)}
            </button>
          ))}
        </div>
        <span className="design-drawer__width" aria-live="polite">
          {Math.round(width)}px
        </span>
        <button
          type="button"
          className="design-drawer__close"
          aria-label={t('website.design.previewClose')}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      <div className={`design-drawer__frame design-drawer__frame--${device}`}>
        {previewURL ? (
          <div className="device-frame__shell">
            <iframe
              ref={setIframeRef}
              src={previewURL}
              title={t('website.design.previewTitle')}
              className="design-drawer__iframe"
            />
          </div>
        ) : (
          <p className="design-drawer__placeholder">
            {t('website.design.previewUnavailable')}
          </p>
        )}
      </div>
    </aside>
  );
}
